import { useCallback, useMemo } from 'react'
import { doc, arrayUnion, arrayRemove, type DocumentReference } from 'firebase/firestore'
import { fbt } from 'fbtee'
import { db } from '../lib/firebase'
import { setDoc } from '../lib/firestore'
import { runMutation } from '../lib/firestoreMutation'
import { useFirestoreDoc } from './useFirestore'
import { track, type ScheduleSurface } from '../lib/analytics'
import type { ScheduleMarkerHides } from '@shared/firestore'

// Ids of hidden lines pile up across festivals; past this the oldest go.
const MAX_HIDDEN_LINES = 500

/**
 * Other people's schedule lines the viewer hid — single lines (per event, by
 * id) and whole authors (across every event). Lives in users/{uid}/private
 * because hiding an author must hold everywhere, and scheduleSettings is
 * deleted by its reset.
 */
export function useScheduleMarkerHides(userId: string | null, surface: ScheduleSurface) {
  const ref = useMemo(
    () =>
      userId
        ? (doc(db, 'users', userId, 'private', 'scheduleMarkerHides') as DocumentReference<ScheduleMarkerHides>)
        : null,
    [userId],
  )
  const { data } = useFirestoreDoc(ref)
  const hiddenMarkerIds = useMemo(() => data?.markerIds ?? [], [data])
  const hiddenAuthorIds = useMemo(() => data?.authorIds ?? [], [data])

  const write = useCallback(
    async (fields: Record<string, unknown>) => {
      if (!ref) return
      await runMutation(
        fbt("Couldn't update hidden lines", 'Error toast when hiding or showing a schedule line fails'),
        () => setDoc(ref, fields, { merge: true }),
      )
    },
    [ref],
  )

  const hideMarker = useCallback(
    async (eventId: string, id: string) => {
      const markerIds = hiddenMarkerIds.length >= MAX_HIDDEN_LINES
        ? [...hiddenMarkerIds.slice(hiddenMarkerIds.length - MAX_HIDDEN_LINES + 1), id]
        : arrayUnion(id)
      await write({ markerIds })
      track('schedule_marker_hidden', { event_id: eventId, surface, kind: 'line' })
    },
    [hiddenMarkerIds, write, surface],
  )
  const hideAuthor = useCallback(
    async (eventId: string, uid: string) => {
      await write({ authorIds: arrayUnion(uid) })
      track('schedule_marker_hidden', { event_id: eventId, surface, kind: 'author' })
    },
    [write, surface],
  )
  const showMarker = useCallback(
    async (eventId: string, id: string) => {
      await write({ markerIds: arrayRemove(id) })
      track('schedule_marker_shown', { event_id: eventId, kind: 'line' })
    },
    [write],
  )
  const showAuthor = useCallback(
    async (eventId: string, uid: string) => {
      await write({ authorIds: arrayRemove(uid) })
      track('schedule_marker_shown', { event_id: eventId, kind: 'author' })
    },
    [write],
  )

  return { hiddenMarkerIds, hiddenAuthorIds, hideMarker, hideAuthor, showMarker, showAuthor }
}
